import React from 'react';
import { Shield, FileCheck, Lock, CheckCircle, ArrowLeft } from 'lucide-react';
import { Link } from 'react-router-dom';
import Navbar from '../../../components/user/Navbar';
import Footer from '../../../components/user/Footer';
import { useScrollAnimation } from '../../../hooks/useScrollAnimation';

const KYCVerification: React.FC = () => {
  const [heroRef, heroVisible] = useScrollAnimation();
  const [featuresRef, featuresVisible] = useScrollAnimation();
  const [benefitsRef, benefitsVisible] = useScrollAnimation();
  const [ctaRef, ctaVisible] = useScrollAnimation();

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      
      {/* Hero Section */}
      <section ref={heroRef} className="pt-32 pb-20 px-4 bg-gradient-to-br from-primary-50 to-white">
        <div className={`max-w-7xl mx-auto transition-all duration-700 ${
          heroVisible ? 'animate-fade-in-up opacity-100' : 'opacity-0'
        }`}>
          <Link 
            to="/services" 
            className="inline-flex items-center text-primary-600 hover:text-primary-700 mb-8 transition-colors"
          >
            <ArrowLeft size={20} className="mr-2" />
            Back to Services
          </Link>
          <div className="flex items-center gap-6 mb-6">
            <div className="bg-primary-100 p-4 rounded-2xl">
              <Shield className="text-primary-600" size={64} />
            </div>
            <div>
              <h1 className="text-5xl md:text-6xl font-bold text-secondary-900 mb-4 tracking-tight">
                KYC & Verification
              </h1>
              <p className="text-xl text-secondary-600">
                Secure business verification system ensuring legitimacy and trust
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Overview Section */}
      <section className="py-20 px-4">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-4xl font-bold text-secondary-900 mb-6">
                Build Trust Through Verification
              </h2>
              <p className="text-lg text-secondary-600 leading-relaxed mb-6">
                Our KYC & Verification system ensures that all businesses on our platform are 
                legitimate and trustworthy. Through comprehensive document verification and 
                identity checks, we maintain the highest standards of security and reliability.
              </p>
              <p className="text-lg text-secondary-600 leading-relaxed">
                For customers, this means peace of mind knowing that every business they interact 
                with has been verified. For businesses, it provides credibility and trust that helps 
                attract more customers.
              </p>
            </div>
            <div className="bg-gradient-to-br from-primary-500 to-primary-600 rounded-2xl p-12 text-white shadow-soft">
              <Lock size={48} className="mb-6" />
              <h3 className="text-2xl font-bold mb-4">Secure & Compliant</h3>
              <p className="text-lg text-white/90 leading-relaxed">
                All verification processes comply with industry standards and regulations. 
                Your documents are stored securely and handled with the utmost care.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section ref={featuresRef} className="py-20 px-4 bg-secondary-50">
        <div className="max-w-7xl mx-auto">
          <div className={`text-center mb-16 transition-all duration-700 ${
            featuresVisible ? 'animate-fade-in-up opacity-100' : 'opacity-0'
          }`}>
            <h2 className="text-4xl font-bold text-secondary-900 mb-4">
              Key Features
            </h2>
            <p className="text-xl text-secondary-600">
              Comprehensive verification tools for security
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              {
                icon: <FileCheck size={40} />,
                title: 'Document Verification',
                description: 'Verify business licenses, registration documents, and identity proofs. Automated and manual review processes.'
              },
              {
                icon: <Shield size={40} />,
                title: 'Business KYC',
                description: 'Complete Know Your Customer verification for businesses. Ensure legitimacy and compliance.'
              },
              {
                icon: <CheckCircle size={40} />,
                title: 'Real-time Status',
                description: 'Track your verification status in real-time. Get updates on approval, rejection, or pending status.'
              },
              {
                icon: <Lock size={40} />,
                title: 'Secure Storage',
                description: 'All documents are encrypted and stored securely. Access is restricted to authorized personnel only.'
              },
              {
                icon: <Shield size={40} />,
                title: 'Identity Verification',
                description: 'Verify business owner identity and authorized representatives. Multi-factor authentication support.'
              },
              {
                icon: <FileCheck size={40} />,
                title: 'Compliance Checks',
                description: 'Automated compliance checks against regulatory requirements. Stay compliant with industry standards.'
              }
            ].map((feature, index) => (
              <div 
                key={index}
                className={`bg-white p-8 rounded-xl border-2 border-secondary-100 hover:border-primary-500 hover:shadow-soft transition-all duration-500 group hover:scale-105 hover:-translate-y-2 ${
                  featuresVisible 
                    ? `animate-fade-in-up opacity-100` 
                    : 'opacity-0'
                }`}
                style={{ transitionDelay: `${index * 100}ms` }}
              >
                <div className="text-primary-500 mb-4 group-hover:scale-110 group-hover:rotate-3 transition-all duration-300">
                  {feature.icon}
                </div>
                <h3 className="text-xl font-bold text-secondary-900 mb-3 group-hover:text-primary-600 transition-colors">
                  {feature.title}
                </h3>
                <p className="text-secondary-600">
                  {feature.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section ref={benefitsRef} className="py-20 px-4">
        <div className="max-w-7xl mx-auto">
          <div className={`text-center mb-16 transition-all duration-700 ${
            benefitsVisible ? 'animate-fade-in-up opacity-100' : 'opacity-0'
          }`}>
            <h2 className="text-4xl font-bold text-secondary-900 mb-4">
              Why KYC Verification Matters?
            </h2>
            <p className="text-xl text-secondary-600">
              Benefits for businesses and customers
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {[
              {
                title: 'Build Customer Trust',
                description: 'Verified businesses gain customer trust and credibility. Show your verification badge to attract more customers.'
              },
              {
                title: 'Platform Security',
                description: 'Maintain a secure platform by ensuring all businesses are legitimate. Protect customers from fraud and scams.'
              },
              {
                title: 'Regulatory Compliance',
                description: 'Stay compliant with financial regulations and industry standards. Meet KYC requirements automatically.'
              },
              {
                title: 'Faster Approvals',
                description: 'Streamlined verification process with automated checks. Get approved faster with our efficient system.'
              },
              {
                title: 'Document Security',
                description: 'Your documents are encrypted and stored securely. Only authorized personnel can access verification data.'
              },
              {
                title: 'Transparent Process',
                description: 'Clear verification requirements and status updates. Know exactly what\'s needed and track your progress.'
              }
            ].map((benefit, index) => (
              <div 
                key={index}
                className={`bg-white p-8 rounded-xl border-2 border-secondary-100 hover:border-primary-500 transition-all duration-500 ${
                  benefitsVisible 
                    ? `animate-fade-in-up opacity-100` 
                    : 'opacity-0'
                }`}
                style={{ transitionDelay: `${index * 100}ms` }}
              >
                <h3 className="text-xl font-bold text-secondary-900 mb-3 flex items-center">
                  <CheckCircle className="text-primary-500 mr-3" size={24} />
                  {benefit.title}
                </h3>
                <p className="text-secondary-600">
                  {benefit.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section ref={ctaRef} className="py-20 px-4 bg-primary-500">
        <div className={`max-w-4xl mx-auto text-center text-white transition-all duration-700 ${
          ctaVisible ? 'animate-fade-in-up opacity-100' : 'opacity-0'
        }`}>
          <h2 className="text-4xl font-bold mb-6 tracking-tight">
            Ready to Get Verified?
          </h2>
          <p className="text-xl mb-8 text-white/90">
            Complete your KYC verification and start building trust with customers
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link
              to="/contact"
              className="bg-white text-primary-600 px-10 py-4 rounded-lg font-bold text-lg hover:bg-secondary-50 transition-all duration-300 shadow-xl hover:scale-110 hover:-translate-y-1"
            >
              Contact Sales
            </Link>
            <Link
              to="/services"
              className="bg-transparent border-2 border-white text-white px-10 py-4 rounded-lg font-bold text-lg hover:bg-white hover:text-primary-600 transition-all duration-300 hover:scale-110 hover:-translate-y-1"
            >
              View All Services
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default KYCVerification;

